import { useTranslation } from 'react-i18next';

import { CopyIconButton } from '~/components/common/copy-icon-button';
import { cn } from '~/utils/cn';
import { formatDateTime, formatRelative } from '~/utils/date';

import { useRunLabels } from '../hooks/use-run-labels';
import type { DeliveryProgress } from '../types/run-detail.api.types';
import { formatCount, percentWidth } from '../utils/run-format';

import { Callout } from './callout';
import { DeliveryRejections } from './delivery-rejections';
import { DeliveryRunProducts } from './delivery-run-products';
import { OperatorEditsDeliveryNote } from './operator-edits-delivery-note';
import { RunStatusChip } from './run-status-chip';

interface Props {
  runId: string;
  /** `progress.delivery` — null until APPROVAL is approved and DELIVERY starts. */
  delivery: DeliveryProgress | null | undefined;
  /** Destination label ("Google Merchant Center") for the headline. */
  destination?: string | null;
  /** `false` where another surface already shows delivered/failed (the Publish pane's metric
   *  strip) — hides the big-number block so each number appears once. */
  showCounts?: boolean;
  /** Inside a stage-pane section: no card chrome or own title (the section supplies both). */
  embedded?: boolean;
}

/**
 * "Did it land?" (R6): the last error / suppression as a Callout on top, the delivered vs
 * failed split drawn as big numbers over one bar (failed only when > 0), one muted line for
 * the attempt (only when > 1), the integration job (copyable) and the last update, then the
 * per-product results from integrations' own ledger (`DeliveryRunProducts`).
 */
export function DeliveryStatusPanel({
  runId,
  delivery,
  destination,
  showCounts = true,
  embedded = false,
}: Props) {
  const { t } = useTranslation('catalog-enrichment-run');
  const { statusLabel } = useRunLabels();
  // null = integrations has not reported yet — show "pending", never a measured-looking 0.
  const reported =
    (delivery?.delivered !== null && delivery?.delivered !== undefined) ||
    (delivery?.failed !== null && delivery?.failed !== undefined);
  const delivered = delivery?.delivered ?? 0;
  const failed = delivery?.failed ?? 0;
  // Items the delivery callback reported but couldn't match to a product. Null (no callback with
  // items yet) is treated as 0 for display; the tile and callout only show when it's above 0.
  // `delivered + failed + dropped` is what the callback reported as its own `in` count.
  const dropped = delivery?.dropped ?? 0;
  const droppedSample = delivery?.droppedSample ?? [];
  const total = delivered + failed + dropped;

  return (
    <div className={cn('flex flex-col', embedded ? 'gap-4' : 'gap-6')}>
      <section
        aria-label={t('delivery.title')}
        className={cn('flex flex-col gap-4', !embedded && 'bg-card rounded-xl border p-4')}
      >
        {embedded ? (
          delivery ? (
            <RunStatusChip status={delivery.status} label={statusLabel(delivery.status)} showIcon />
          ) : null
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-foreground text-sm font-semibold">
              {destination ? t('delivery.titleTo', { destination }) : t('delivery.title')}
            </h3>
            {delivery ? (
              <RunStatusChip
                status={delivery.status}
                label={statusLabel(delivery.status)}
                showIcon
              />
            ) : null}
          </div>
        )}
        {!delivery ? (
          <p className="text-muted-foreground text-sm">{t('delivery.notStarted')}</p>
        ) : (
          <>
            {delivery.lastError ? (
              <Callout tone="error" title={t('delivery.lastErrorTitle')}>
                {delivery.lastError}
              </Callout>
            ) : null}
            {delivery.suppressed ? (
              <Callout tone="info" title={t('delivery.suppressedTitle')}>
                {t('delivery.suppressedHint')}
              </Callout>
            ) : null}
            {/* A11 — operator edits integrations applied on this push, or the honest warning
            that generated values went out instead; nothing on an older backend. */}
            <OperatorEditsDeliveryNote
              edits={delivery.operatorEdits}
              deliveryAttempt={delivery.attempt}
            />
            {!reported ? (
              <p className="text-muted-foreground text-sm">{t('delivery.countsPending')}</p>
            ) : !showCounts ? null : (
              <div className="flex flex-wrap items-end gap-x-8 gap-y-3">
                <div className="flex flex-col">
                  <span className="text-muted-foreground text-xs">{t('delivery.delivered')}</span>
                  <span className="text-foreground text-3xl leading-tight font-semibold tabular-nums">
                    {formatCount(delivery.delivered)}
                  </span>
                </div>
                {failed > 0 ? (
                  <div className="flex flex-col">
                    <span className="text-muted-foreground text-xs">{t('delivery.failed')}</span>
                    <span className="text-error text-3xl leading-tight font-semibold tabular-nums">
                      {formatCount(failed)}
                    </span>
                  </div>
                ) : null}
                {/* Coordinator follow-up (b) — hidden when null/0; shown, it reconciles with
                delivered + failed against what the callback reported as its own `in` count. */}
                {dropped > 0 ? (
                  <div className="flex flex-col">
                    <span className="text-muted-foreground text-xs">{t('delivery.dropped')}</span>
                    <span className="text-warning text-3xl leading-tight font-semibold tabular-nums">
                      {formatCount(dropped)}
                    </span>
                  </div>
                ) : null}
                {total > 0 ? (
                  <div
                    aria-hidden="true"
                    className="bg-muted mb-2 flex h-2.5 min-w-40 flex-1 overflow-hidden rounded-full"
                  >
                    <span
                      className="bg-primary/60 h-full"
                      style={{ width: percentWidth(delivered, total) }}
                    />
                    <span
                      className="bg-error h-full"
                      style={{ width: percentWidth(failed, total) }}
                    />
                    {dropped > 0 ? (
                      <span
                        className="bg-warning h-full"
                        style={{ width: percentWidth(dropped, total) }}
                      />
                    ) : null}
                  </div>
                ) : null}
              </div>
            )}
            {dropped > 0 ? (
              <Callout tone="warning" title={t('delivery.droppedCallout', { count: dropped })}>
                <span className="flex flex-col gap-1">
                  <span>{t('delivery.droppedBody')}</span>
                  {droppedSample.length > 0 ? (
                    <span className="inline-flex flex-wrap items-center gap-1">
                      <span className="font-mono text-xs break-all">
                        {droppedSample.join(', ')}
                      </span>
                      <CopyIconButton
                        text={droppedSample.join(', ')}
                        label={t('delivery.droppedSampleCopyLabel')}
                        className="shrink-0"
                      />
                    </span>
                  ) : null}
                </span>
              </Callout>
            ) : null}
            <p className="text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
              {delivery.attempt > 1 ? (
                <>
                  <span>{t('delivery.attemptN', { attempt: delivery.attempt })}</span>
                  <span aria-hidden="true">·</span>
                </>
              ) : null}
              {delivery.externalJobRef ? (
                <span className="inline-flex items-center gap-1">
                  {t('delivery.externalJobRef')}
                  <span className="text-foreground font-mono">
                    {delivery.externalJobRef.length > 14
                      ? `${delivery.externalJobRef.slice(0, 10)}…`
                      : delivery.externalJobRef}
                  </span>
                  <CopyIconButton
                    text={delivery.externalJobRef}
                    label={delivery.externalJobRef}
                    className="shrink-0"
                  />
                </span>
              ) : null}
              {delivery.updatedAt ? (
                <>
                  {delivery.externalJobRef ? <span aria-hidden="true">·</span> : null}
                  <span title={formatDateTime(delivery.updatedAt)}>
                    {t('delivery.updatedWhen', { when: formatRelative(delivery.updatedAt) })}
                  </span>
                </>
              ) : null}
            </p>
          </>
        )}
      </section>

      <section className={cn(embedded ? 'border-t pt-3' : 'bg-card rounded-xl border p-4')}>
        <DeliveryRunProducts externalJobRef={delivery?.externalJobRef} />
      </section>

      {/* Finding 13 — catalog-service's own per-product rejections (B1), separate from
      integrations' ledger above: it is what triggers "held back" without a dig through logs. */}
      <section className={cn(embedded ? 'border-t pt-3' : 'bg-card rounded-xl border p-4')}>
        <DeliveryRejections runId={runId} enabled={!!delivery} />
      </section>
    </div>
  );
}
